import React, {useEffect, useState} from 'react';
import Layout from '../components/Layout';
import {api} from '../../api';

export default function OnboardingManager() {
  const [tab, setTab] = useState('onboarding');
  const [checklists, setChecklists] = useState([]);
  const [form, setForm] = useState(null);

  const load = () => api('/hcm/onboarding-checklists').then(r => r.json()).then(setChecklists).catch(() => {});
  useEffect(load, []);

  const filtered = checklists.filter(c => (c.checklistType || 'onboarding') === tab);

  const handleSave = async (e) => {
    e.preventDefault();
    const body = {name: form.name, items: form.items.filter(i => i.title.trim())};
    if (form.id) await api(`/hcm/onboarding-checklists/${form.id}`, {method: 'PUT', body: JSON.stringify(body)});
    else await api('/hcm/onboarding-checklists', {method: 'POST', body: JSON.stringify(body)});
    setForm(null);
    load();
  };

  const addItem = () => setForm({...form, items: [...form.items, {title: '', description: ''}]});
  const removeItem = (i) => setForm({...form, items: form.items.filter((_, idx) => idx !== i)});

  return (
    <Layout>
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 md:p-6 border-b flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">Onboarding & Offboarding</h1>
          <button onClick={() => setForm({name: '', checklistType: tab, items: [{title: '', description: ''}]})}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm">New Checklist</button>
        </div>
        <div className="border-b">
          <nav className="flex gap-6 px-6">
            {[['onboarding','Onboarding'],['offboarding','Offboarding']].map(([key, label]) => (
              <button key={key} onClick={() => setTab(key)}
                className={`py-3 text-sm font-medium border-b-2 ${tab === key ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>{label}</button>
            ))}
          </nav>
        </div>
        <div className="p-4 md:p-6">
          {filtered.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No {tab} checklists yet. Create one to get started.</p>
          ) : (
            <div className="space-y-4">
              {filtered.map(c => (
                <div key={c.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-900">{c.name}</h3>
                    <div className="flex gap-2">
                      <button onClick={() => setForm({id: c.id, name: c.name, items: c.items?.length ? c.items : [{title: '', description: ''}]})}
                        className="text-sm text-gray-500 hover:text-indigo-600">Edit</button>
                      <button onClick={async () => { await api(`/hcm/onboarding-checklists/${c.id}`, {method: 'DELETE'}); load(); }}
                        className="text-sm text-gray-500 hover:text-red-600">Delete</button>
                    </div>
                  </div>
                  {c.items && c.items.length > 0 && (
                    <ol className="space-y-1">
                      {c.items.map((item, i) => (
                        <li key={item.id || i} className="flex items-start gap-2 text-sm">
                          <span className="text-gray-400 font-mono w-5 flex-shrink-0">{i + 1}.</span>
                          <div>
                            <span className="text-gray-900">{item.title}</span>
                            {item.description && <p className="text-gray-500 text-xs">{item.description}</p>}
                          </div>
                        </li>
                      ))}
                    </ol>
                  )}
                  <p className="text-xs text-gray-400 mt-2">{c.items?.length || 0} items</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {form && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setForm(null)}>
          <form onSubmit={handleSave} onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg space-y-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900">{form.id ? 'Edit' : 'New'} Checklist</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input required value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="e.g. New Hire Onboarding" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Items</label>
              {form.items.map((item, i) => (
                <div key={i} className="flex gap-2 mb-2">
                  <div className="flex-1 space-y-1">
                    <input value={item.title} onChange={e => { const items = [...form.items]; items[i] = {...items[i], title: e.target.value}; setForm({...form, items}); }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm" placeholder="Task title" />
                    <input value={item.description || ''} onChange={e => { const items = [...form.items]; items[i] = {...items[i], description: e.target.value}; setForm({...form, items}); }}
                      className="w-full px-3 py-1 border border-gray-200 rounded text-xs text-gray-500 focus:ring-1 focus:ring-indigo-500 outline-none" placeholder="Description (optional)" />
                  </div>
                  <button type="button" onClick={() => removeItem(i)} className="text-red-400 hover:text-red-600 self-start mt-2">×</button>
                </div>
              ))}
              <button type="button" onClick={addItem} className="text-sm text-indigo-600 hover:text-indigo-800">+ Add item</button>
            </div>
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setForm(null)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">Save</button>
            </div>
          </form>
        </div>
      )}
    </Layout>
  );
}
